import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Bell, CheckCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(async () => {
    try { setCount((await axios.get(`${API}/notifications/unread-count`, authHeaders())).data.count); } catch (e) {}
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  const loadList = async () => {
    try { setItems((await axios.get(`${API}/notifications`, authHeaders())).data); } catch (e) {}
  };

  const onOpenChange = (v) => { setOpen(v); if (v) loadList(); };

  const clickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await axios.post(`${API}/notifications/${n.id}/read`, {}, authHeaders()); } catch (e) {}
      fetchCount();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await axios.post(`${API}/notifications/read-all`, {}, authHeaders()); } catch (e) {}
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setCount(0);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-gray-500 hover:text-rose-700 hover:bg-rose-50" data-testid="notif-bell">
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notif-menu">
        <div className="flex items-center justify-between px-4 py-3 border-b border-rose-100">
          <span className="font-semibold text-rose-900 text-sm">Notifications</span>
          <button onClick={markAll} className="text-xs text-rose-600 flex items-center gap-1 hover:underline" data-testid="notif-mark-all">
            <CheckCheck className="w-3.5 h-3.5" /> Mark all read
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8" data-testid="notif-empty">You're all caught up ✨</p>
          ) : items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              onClick={() => clickItem(n)}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-rose-50 transition-colors ${n.read ? 'opacity-60' : 'bg-rose-50/40'}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-rose-500 mt-1.5 shrink-0" />}
                <div className={n.read ? 'pl-4' : ''}>
                  <p className="text-sm font-medium text-rose-900">{n.title}</p>
                  {n.body && <p className="text-xs text-gray-600 mt-0.5">{n.body}</p>}
                  <p className="text-[11px] text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default NotificationBell;
